import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface DailyReport {
  id: string;
  trip_id: string;
  report_date: string;
  day_number: number;
  hours_on_site: string | null;
  labor_hours: number | null;
  crew_on_site: string | null;
  work_completed: string | null;
  tomorrow_plan: string | null;
  internal_notes: string | null;
  punch_list: string | null;
  lead_captured: boolean | null;
  submitted_at: string | null;
  delivered_to_customer_at: string | null;
  created_at: string;
  updated_at: string;
}

export const DAILY_REPORT_COLUMNS =
  'id, trip_id, report_date, day_number, hours_on_site, labor_hours, crew_on_site, work_completed, tomorrow_plan, internal_notes, punch_list, lead_captured, submitted_at, delivered_to_customer_at, created_at, updated_at';

export function dailyReportsQueryKey(tripId: string | undefined) {
  return ['daily-reports', tripId] as const;
}

export function useDailyReports(tripId: string | undefined) {
  return useQuery({
    queryKey: dailyReportsQueryKey(tripId),
    enabled: !!tripId,
    queryFn: async () => {
      if (!tripId) throw new Error('Trip ID required');
      const { data, error } = await supabase
        .from('daily_reports')
        .select(DAILY_REPORT_COLUMNS)
        .eq('trip_id', tripId)
        .order('report_date', { ascending: true });
      if (error) throw error;
      return (data ?? []) as DailyReport[];
    },
    staleTime: 60 * 1000,
  });
}

/** Status label for a report row, derived only from the timestamps that exist. */
export function reportStatus(report: Pick<DailyReport, 'submitted_at' | 'delivered_to_customer_at'>): 'draft' | 'submitted' | 'delivered' {
  if (report.delivered_to_customer_at) return 'delivered';
  if (report.submitted_at) return 'submitted';
  return 'draft';
}

/** Latest report by date, or null. */
export function latestReport(reports: DailyReport[]): DailyReport | null {
  if (reports.length === 0) return null;
  return reports.reduce((latest, r) => (r.report_date > latest.report_date ? r : latest), reports[0]);
}
